/**
 * Tracking Service for Netlizing
 *
 * This service handles collecting and sending purchase tracking data to the server.
 * It uses the low-level utility functions from tracking.js but contains the
 * business logic and API calls.
 */

import axios from "axios";
import {
    generateClientId,
    getUrlParameters,
    collectTrackingData,
    getCustomParams,
    passParam,
    storeLocalValue,
    getLocalValue,
    extractNumericValue,
    hasMarketingConsent
} from "../utils/tracking";
import { formatPhoneToInternational } from "../utils/phoneFormatter";

// Constants
const STORAGE_PREFIX = "nl_";
const API_ENDPOINT = "/api/purchase-data-receiver";
const DEFAULT_CURRENCY = "HUF";
const GA4_PROPERTY_ID = "G-SLCF7DW5M3"; // GA4 property ID

/**
 * Get the session ID using Google Analytics gtag
 * Returns a Promise that resolves with the session_id
 *
 * @returns {Promise<string>} Promise that resolves with the session ID
 */
export function getSessionId() {
    // Set up dataLayer if it doesn't exist
    window.dataLayer = window.dataLayer || [];
    function gtag() {
        dataLayer.push(arguments);
    }
    window.gtag = gtag;

    return new Promise(resolve => {
        // Try to get session ID from cookie first (more reliable)
        const sessionIdFromCookie = getSessionIdFromCookie();
        if (sessionIdFromCookie) {
            // Store the session ID for future use
            storeLocalValue("session_id", sessionIdFromCookie, STORAGE_PREFIX);
            resolve(sessionIdFromCookie);
            return;
        }

        // Add a timeout to prevent hanging indefinitely
        const timeoutId = setTimeout(() => {
            console.warn("GA4 session_id request timed out");
            // Fallback to stored session ID or generate a new one
            const storedSessionId = getLocalValue("session_id", STORAGE_PREFIX);
            if (storedSessionId) {
                resolve(storedSessionId);
            } else {
                const newSessionId = generateClientId();
                storeLocalValue("session_id", newSessionId, STORAGE_PREFIX);
                resolve(newSessionId);
            }
        }, 2000); // 2 second timeout

        try {
            gtag("get", GA4_PROPERTY_ID, "session_id", sessionId => {
                // Clear the timeout since we got a response
                clearTimeout(timeoutId);

                if (sessionId) {
                    // Store the session ID for future use
                    storeLocalValue("session_id", sessionId, STORAGE_PREFIX);
                    resolve(sessionId);
                } else {
                    // Fallback to stored session ID or generate a new one
                    const storedSessionId = getLocalValue(
                        "session_id",
                        STORAGE_PREFIX
                    );
                    if (storedSessionId) {
                        resolve(storedSessionId);
                    } else {
                        const newSessionId = generateClientId();
                        storeLocalValue(
                            "session_id",
                            newSessionId,
                            STORAGE_PREFIX
                        );
                        resolve(newSessionId);
                    }
                }
            });
        } catch (error) {
            // Clear the timeout if there's an error
            clearTimeout(timeoutId);

            // Fallback if there's an error
            const storedSessionId = getLocalValue("session_id", STORAGE_PREFIX);
            if (storedSessionId) {
                resolve(storedSessionId);
            } else {
                const newSessionId = generateClientId();
                storeLocalValue("session_id", newSessionId, STORAGE_PREFIX);
                resolve(newSessionId);
            }
        }
    });
}

/**
 * Get the session ID directly from the GA4 cookie
 * This is more reliable than using the gtag API
 *
 * @returns {string|null} The session ID or null if not found
 */
export function getSessionIdFromCookie() {
    try {
        // Extract the GA4 property ID without the 'G-' prefix
        const propertyIdSuffix = GA4_PROPERTY_ID.replace("G-", "");
        const cookieName = `_ga_${propertyIdSuffix}`;

        // Find the GA4 cookie
        const cookies = document.cookie.split("; ");
        const ga4Cookie = cookies.find(cookie =>
            cookie.startsWith(`${cookieName}=`)
        );

        if (!ga4Cookie) {
            return null;
        }

        // Get the cookie value
        const cookieValue = ga4Cookie.split("=")[1];

        // The format is either:
        // 1. New format (May 2023+): GS2.1.s<timestamp>$o<session_count>$g<engaged>$t<timestamp>
        // 2. Older format: GS1.1.<session_id>.<session_number>

        if (cookieValue.startsWith("GS2.1.")) {
            // New format - extract the session_id from after 's' and before '$'
            const parts = cookieValue.replace("GS2.1.", "").split("$");
            const sessionPart = parts.find(part => part.startsWith("s"));

            if (sessionPart) {
                return sessionPart.substring(1); // Remove the 's' prefix
            }
        } else if (cookieValue.startsWith("GS1.1.")) {
            // Old format - session ID is the third part
            const parts = cookieValue.split(".");
            if (parts.length >= 3) {
                return parts[2];
            }
        }

        return null;
    } catch (error) {
        console.error("Error extracting session ID from cookie:", error);
        return null;
    }
}

/**
 * Get the client ID from cookie or generate a new one
 *
 * @returns {string} Client ID
 */
export function getClientId() {
    // Try to get client ID from localStorage first (may have been set by server)
    let clientId = getLocalValue("client_id", STORAGE_PREFIX);

    if (!clientId) {
        // NOTE: FPID cookie has HttpOnly flag and can only be read by the server
        // Client ID should be extracted server-side and passed to the frontend
        // Format: FPID2.2.C45AWfbdtVXvtoMTsU9NxkvcE658Ohym2VjuhrvNH9g%3D.1695715374
        // Valid client_id: C45AWfbdtVXvtoMTsU9NxkvcE658Ohym2VjuhrvNH9g%3D.1695715374

        // If no client ID is available, generate one as specified
        clientId = generateClientId();
        storeLocalValue("client_id", clientId, STORAGE_PREFIX);
    }

    return clientId;
}

/**
 * Store GTM preview parameter for server-side GTM debugging
 *
 * @param {string} previewParam - The GTM preview parameter
 */
export function storeGtmPreviewParam(previewParam) {
    if (previewParam) {
        // Fix base64 padding before storing
        const fixedParam = fixBase64Padding(previewParam);
        passParam({ gtm_preview: fixedParam });
        storeLocalValue("gtm_preview", fixedParam, STORAGE_PREFIX);
    }
}

/**
 * Get the stored GTM preview parameter
 *
 * @returns {string} The GTM preview parameter
 */
/**
 * Fix base64 padding if missing
 * @param {string} base64String - The base64 string that might be missing padding
 * @returns {string} The base64 string with proper padding
 */
function fixBase64Padding(base64String) {
    if (!base64String) return base64String;
    
    // Add padding if needed
    const missingPadding = base64String.length % 4;
    if (missingPadding) {
        return base64String + '='.repeat(4 - missingPadding);
    }
    return base64String;
}

export function getGtmPreviewParam() {
    // Check URL parameters first
    const urlParams = getUrlParameters();
    let previewParam = urlParams.gtm_debug || urlParams.gtm_preview || "";

    if (previewParam) {
        // Fix base64 padding if missing
        previewParam = fixBase64Padding(previewParam);
        // Store it for future use
        storeGtmPreviewParam(previewParam);
        return previewParam;
    }

    // Check custom params (both gtm_preview and gtm_debug)
    const customParams = getCustomParams();
    if (customParams.gtm_preview) {
        return fixBase64Padding(customParams.gtm_preview);
    }
    if (customParams.gtm_debug) {
        return fixBase64Padding(customParams.gtm_debug);
    }

    // Check localStorage
    const storedParam = getLocalValue("gtm_preview", STORAGE_PREFIX) || "";
    return fixBase64Padding(storedParam);
}

/**
 * Get user data from the global Netlizing object
 *
 * @returns {Object} User data object
 */
function getNetlizingUserData() {
    return window.netlizing?.user || {};
}

/**
 * Build a tracking payload with all required data
 *
 * @param {Object} options - Options for the payload
 * @returns {Object} The complete tracking payload
 */
async function buildTrackingPayload(options = {}) {
    const clientId = getClientId();
    const sessionId = await getSessionId();
    const trackingData = collectTrackingData();
    const previewParam = getGtmPreviewParam();
    const netlizingUser = getNetlizingUserData();

    // Get marketing consent status
    const marketingConsent = hasMarketingConsent();

    // Start with basic tracking data
    const payload = {
        client_id: clientId,
        session_id: sessionId,
        _gclid: trackingData._gclid,
        _fbp: trackingData._fbp,
        _fbc: trackingData._fbc,
        user_agent: trackingData.user_agent,
        preview_param: previewParam,
        currency: options.currency || DEFAULT_CURRENCY,
        value: options.value || "0",
        marketing_consent: marketingConsent ? "granted" : "denied"
    };

    // Add quotation_id if available
    if (options.quotationId) {
        payload.quotation_id = options.quotationId;
    }

    // Add user data if available
    const userData = options.userData || {};

    // Only add user data if consent is given
    if (options.userDataConsent !== false) {
        // Email priority: 1) userData.email, 2) netlizingUser.email
        if (userData.email || netlizingUser.email) {
            payload.email = userData.email || netlizingUser.email;
        }

        // Phone priority: 1) userData.phone, 2) netlizingUser.phone
        // Format phone to international format
        if (userData.phone || netlizingUser.phone) {
            const rawPhone = userData.phone || netlizingUser.phone;
            payload.phone = formatPhoneToInternational(rawPhone);
        }

        // First name priority: 1) userData.first_name, 2) netlizingUser.first_name
        if (userData.first_name || netlizingUser.first_name) {
            payload.first_name =
                userData.first_name || netlizingUser.first_name;
        }

        // Last name priority: 1) userData.last_name, 2) netlizingUser.last_name
        if (userData.last_name || netlizingUser.last_name) {
            payload.last_name = userData.last_name || netlizingUser.last_name;
        }

        // Company priority: 1) userData.company, 2) netlizingUser.company
        if (userData.company || netlizingUser.company) {
            payload.company = userData.company || netlizingUser.company;
        }
    }

    return payload;
}

/**
 * Send a tracking payload to the server
 *
 * @param {Object} payload - The payload to send
 * @returns {Promise} Promise that resolves with the server response
 */
async function sendPayload(payload) {
    try {
        const response = await axios.post(API_ENDPOINT, payload);
        return response;
    } catch (error) {
        console.error("Error sending tracking data:", error);
        throw error;
    }
}

/**
 * Asynchronously gathers all tracking data and sends it to the backend
 *
 * @returns {Promise} - Promise that resolves with the server response
 */
export async function sendTrackingData() {
    try {
        const payload = await buildTrackingPayload({
            value: "0",
            currency: DEFAULT_CURRENCY
        });

        return await sendPayload(payload);
    } catch (error) {
        console.error("Error sending tracking data:", error);
        throw error;
    }
}

/**
 * Send purchase tracking data to the server
 *
 * @param {Object} options - Tracking options
 * @param {Object} userData - Optional user data (needs consent)
 * @param {Object} transactionData - Optional transaction data
 * @returns {Promise} - Promise from the axios request
 */
export async function trackPurchase(
    options = {},
    userData = {},
    transactionData = {}
) {
    try {
        const payload = await buildTrackingPayload({
            userData: userData,
            userDataConsent: options.userDataConsent !== false,
            value: transactionData.value || "0",
            currency: transactionData.currency || DEFAULT_CURRENCY,
            quotationId: options.quotationId || transactionData.quotationId
        });

        return await sendPayload(payload);
    } catch (error) {
        console.error("Error sending tracking data:", error);
        throw error;
    }
}

/**
 * Track a form submission event
 *
 * @param {string} formId - Identifier for the form
 * @param {Object} formData - The form data
 * @param {Object} options - Additional tracking options
 * @returns {Promise} - Promise that resolves when tracking is complete
 */
export async function trackFormSubmission(formId, formData = {}, options = {}) {
    try {
        // Extract user data from form if available and format phone number
        const userData = {
            email: formData.email,
            phone: formatPhoneToInternational(
                formData.phone || formData.telephone
            ),
            first_name: formData.first_name || formData.forename,
            last_name: formData.last_name || formData.surname,
            company: formData.company || formData.company_name
        };

        // Define form IDs that should NOT include quotation_id and value
        const excludedFormIds = [
            "footer-contact",
            "sticky-button-offer",
            "registercta"
        ];

        let transactionValue = "0";
        let quotationId = null;

        // Only extract and include quotation_id and value for car lead related forms
        if (!excludedFormIds.includes(formId)) {
            // Determine if this form is related to a deal and extract the transaction value
            transactionValue = options.value || formData.value || "0";

            // If we're on a confirmation page, try to extract the value from the page
            const pageValue = extractTransactionValueFromPage();
            if (pageValue !== "0") {
                transactionValue = pageValue;
            }

            // Extract the calculation ID (quotation_id) from:
            // 1. Options if provided
            // 2. Form data if available
            // 3. URL parameter 'lead'
            // 4. Page content (Kalkuláció azonosító element)
            quotationId =
                options.quotationId ||
                formData.quotation_id ||
                formData.offer_id;

            // If not found in options or form data, try to get from URL
            if (!quotationId) {
                const urlParams = new URLSearchParams(window.location.search);
                quotationId = urlParams.get("lead");
            }

            // If still not found, try to get from page content
            if (!quotationId) {
                // Look for the calculation ID in the page (usually displayed as "Kalkuláció azonosító: DIP123")
                const calculationElements = document.querySelectorAll(".mt-3");
                for (const element of calculationElements) {
                    if (element.textContent.includes("Kalkuláció azonosító")) {
                        const match = element.textContent.match(/([A-Z]+\d+)/);
                        if (match && match[1]) {
                            quotationId = match[1];
                            break;
                        }
                    }
                }
            }
        }

        // Return the promise so it can be awaited
        return await trackPurchase(
            {
                formId,
                userDataConsent: options.userDataConsent !== false,
                quotationId: quotationId
            },
            userData,
            {
                currency: options.currency || DEFAULT_CURRENCY,
                value: transactionValue,
                quotationId: quotationId
            }
        );
    } catch (error) {
        console.error("Error during form submission tracking:", error);
        // Even if tracking fails, we don't want to block the user
        return null;
    }
}

/**
 * Extract transaction value from the confirmation page
 *
 * @returns {string} The extracted transaction value or "0" if not found
 */
function extractTransactionValueFromPage() {
    // Check if we're on a confirmation page with a lead ID
    const urlParams = getUrlParameters();
    if (!urlParams.lead) return "0";

    // Try to get the value from the page content (monthly payment value)
    const monthlyPaymentElement = document.querySelector(
        ".offer-summary-row.highlighted .col-value"
    );

    if (!monthlyPaymentElement) return "0";

    return extractNumericValue(monthlyPaymentElement.textContent);
}
